import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  User, Mail, Phone, MapPin, Building2, Briefcase, Calendar, 
  Save, ArrowLeft, Edit2, CheckCircle, AlertCircle, Shield, Lock, Key
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import ChangePasswordModal from '../components/ChangePasswordModal';

const MyProfile = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    mobile: '',
    personal_email: '',
    current_address: '',
    permanent_address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
  });

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/profile/me');
      const data = response.data;
      setProfile(data);
      setFormData({
        email: data.email || '',
        phone: data.phone || '',
        mobile: data.mobile || data.phone || '',
        personal_email: data.personal_email || '',
        current_address: data.current_address || data.address || '',
        permanent_address: data.permanent_address || '',
        emergency_contact_name: data.emergency_contact_name || '',
        emergency_contact_phone: data.emergency_contact_phone || '',
        emergency_contact_relation: data.emergency_contact_relation || '',
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      // Use auth context user data as fallback
      setProfile({
        name: user?.name,
        email: user?.email,
        role: user?.role,
        employee_id: user?.employee_id,
      });
      setFormData({
        email: user?.email || '',
        phone: '',
        mobile: '',
        personal_email: '',
        current_address: '',
        permanent_address: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        emergency_contact_relation: '',
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put('/profile/me', formData);
      setProfile(prev => ({ ...prev, ...formData }));
      setIsEditing(false);
      toast.success('Profile updated successfully');
      
      // Update auth context if email changed
      if (formData.email !== user?.email) {
        updateUser?.({ ...user, email: formData.email });
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original profile values
    setFormData({
      email: profile?.email || '',
      phone: profile?.phone || '',
      mobile: profile?.mobile || profile?.phone || '',
      personal_email: profile?.personal_email || '',
      current_address: profile?.current_address || profile?.address || '',
      permanent_address: profile?.permanent_address || '',
      emergency_contact_name: profile?.emergency_contact_name || '',
      emergency_contact_phone: profile?.emergency_contact_phone || '',
      emergency_contact_relation: profile?.emergency_contact_relation || '',
    });
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="my-profile-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">My Profile</h1>
            <p className="text-sm text-slate-500">View and update your personal information</p>
          </div>
        </div>
        {!isEditing ? (
          <Button onClick={() => setIsEditing(true)} className="gap-2">
            <Edit2 className="w-4 h-4" />
            Edit Profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? (
                <div className="spinner w-4 h-4"></div>
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="lg:col-span-1">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-full bg-slate-900 flex items-center justify-center mb-4">
                <span className="text-3xl font-semibold text-white">
                  {profile?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <h2 className="text-xl font-semibold text-slate-900">{profile?.name || 'User'}</h2>
              <p className="text-sm text-slate-500 mt-1">{profile?.designation || profile?.role}</p>
              <Badge className="mt-2 capitalize" variant="secondary">
                <Shield className="w-3 h-3 mr-1" />
                {profile?.role || 'Employee'}
              </Badge>
              
              <div className="w-full mt-6 pt-6 border-t border-slate-200 space-y-3">
                {profile?.employee_code && (
                  <div className="flex items-center gap-3 text-sm">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Emp Code: {profile.employee_code}</span>
                  </div>
                )}
                {profile?.department && (
                  <div className="flex items-center gap-3 text-sm">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">{profile.department}</span>
                  </div>
                )}
                {profile?.date_of_joining && (
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">Joined: {new Date(profile.date_of_joining).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Key className="w-5 h-5 text-slate-500" />
              Security
            </CardTitle>
            <CardDescription>Manage your account security</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              variant="outline" 
              onClick={() => setShowChangePassword(true)}
              className="w-full gap-2"
              data-testid="change-password-btn"
            >
              <Lock className="w-4 h-4" />
              Change Password
            </Button>
          </CardContent>
        </Card>

        {/* Editable Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-slate-500" />
                Contact Information
              </CardTitle>
              <CardDescription>Update your email and phone numbers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Official Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={!isEditing}
                    placeholder="your.email@company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="personal_email">Personal Email</Label>
                  <Input
                    id="personal_email"
                    type="email"
                    value={formData.personal_email}
                    onChange={(e) => handleInputChange('personal_email', e.target.value)}
                    disabled={!isEditing}
                    placeholder="personal@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Office Phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    disabled={!isEditing}
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobile">Mobile Number</Label>
                  <Input
                    id="mobile"
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                    disabled={!isEditing}
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-slate-500" />
                Address Information
              </CardTitle>
              <CardDescription>Update your current and permanent address</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_address">Current Address</Label>
                <Textarea
                  id="current_address"
                  value={formData.current_address}
                  onChange={(e) => handleInputChange('current_address', e.target.value)}
                  disabled={!isEditing}
                  placeholder="Enter your current residential address"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="permanent_address">Permanent Address</Label>
                <Textarea
                  id="permanent_address"
                  value={formData.permanent_address}
                  onChange={(e) => handleInputChange('permanent_address', e.target.value)}
                  disabled={!isEditing}
                  placeholder="Enter your permanent address"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Emergency Contact */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-slate-500" />
                Emergency Contact
              </CardTitle>
              <CardDescription>Contact person in case of emergency</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emergency_name">Contact Name</Label>
                  <Input
                    id="emergency_name"
                    value={formData.emergency_contact_name}
                    onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
                    disabled={!isEditing}
                    placeholder="Full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency_phone">Contact Phone</Label>
                  <Input
                    id="emergency_phone"
                    type="tel"
                    value={formData.emergency_contact_phone}
                    onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
                    disabled={!isEditing}
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emergency_relation">Relationship</Label>
                  <Input
                    id="emergency_relation"
                    value={formData.emergency_contact_relation}
                    onChange={(e) => handleInputChange('emergency_contact_relation', e.target.value)}
                    disabled={!isEditing}
                    placeholder="e.g., Spouse, Parent"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Read-only Information */}
          <Card className="bg-slate-50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-slate-500" />
                Basic Information
                <Badge variant="outline" className="ml-2">Read Only</Badge>
              </CardTitle>
              <CardDescription>These details can only be updated by HR/Admin</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-500">Full Name</Label>
                  <p className="text-sm font-medium text-slate-900">{profile?.name || '-'}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-500">Employee Code</Label>
                  <p className="text-sm font-medium text-slate-900">{profile?.employee_code || profile?.login_id || '-'}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-500">Department</Label>
                  <p className="text-sm font-medium text-slate-900">{profile?.department || '-'}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-500">Designation</Label>
                  <p className="text-sm font-medium text-slate-900">{profile?.designation || '-'}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-500">Date of Joining</Label>
                  <p className="text-sm font-medium text-slate-900">
                    {profile?.date_of_joining ? new Date(profile.date_of_joining).toLocaleDateString() : '-'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-500">Reporting Manager</Label>
                  <p className="text-sm font-medium text-slate-900">{profile?.reporting_manager || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={showChangePassword}
        onOpenChange={setShowChangePassword}
        onSuccess={() => {
          toast.success('Password updated successfully');
        }}
      />
    </div>
  );
};

export default MyProfile;
